import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  Calendar, Eye, Clock, ArrowLeft, ChevronRight, Tag,
  User, ExternalLink, BookOpen, List, LayoutGrid, FileText, MapPin
} from 'lucide-react';
import SEO from '../components/SEO';
import { blogPostingLD, breadcrumbLD, collectionPageLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const styleLabels = {
  standard: { label: 'Editorial', color: 'bg-blue-500/20 text-blue-400', icon: FileText },
  listicle: { label: 'Listicle', color: 'bg-amber-500/20 text-amber-400', icon: List },
  showcase: { label: 'Showcase', color: 'bg-purple-500/20 text-purple-400', icon: LayoutGrid },
};


// ============= BANNER AD COMPONENT =============
function BannerAd({ ad }) {
  if (!ad) return null;
  return (
    <Link to={ad.link} className="block my-8" data-testid={`banner-ad-${ad.ad_id}`}>
      <div className="relative rounded-xl overflow-hidden border border-border bg-gradient-to-r from-indigo-500/10 via-slate-900 to-purple-500/10 hover:border-indigo-500/30 transition-all group">
        <div className="flex items-center gap-4 p-4">
          {ad.image_url && (
            <img src={ad.image_url} alt={ad.name} className="w-20 h-20 rounded-lg object-cover flex-shrink-0 group-hover:scale-105 transition-transform" />
          )}
          <div className="flex-1 min-w-0">
            <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Sponsored</span>
            <h4 className="text-base font-bold text-foreground mt-0.5">{ad.name}</h4>
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{ad.description}</p>
          </div>
          <div className="text-right flex-shrink-0">
            {ad.base_price > 0 && <p className="text-lg font-bold text-emerald-400">${ad.base_price}</p>}
            <span className="inline-flex items-center gap-1 text-xs text-indigo-400 font-medium group-hover:underline">
              {ad.cta} <ChevronRight className="h-3 w-3" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}


// ============= SQUARE AD COMPONENT =============
function SquareAd({ ad }) {
  if (!ad) return null;
  return (
    <Link to={ad.link} className="block" data-testid={`square-ad-${ad.ad_id}`}>
      <Card className="border-border bg-muted/60 hover:border-indigo-500/30 transition-all group overflow-hidden">
        <CardContent className="p-0">
          {ad.image_url && (
            <img src={ad.image_url} alt={ad.name} className="w-full h-32 object-cover group-hover:scale-105 transition-transform" />
          )}
          <div className="p-3">
            <span className="text-[8px] font-semibold text-muted-foreground uppercase tracking-wider">Sponsored</span>
            <h4 className="text-sm font-bold text-foreground mt-0.5 line-clamp-1">{ad.name}</h4>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ad.description}</p>
            {ad.base_price > 0 && <p className="text-sm font-bold text-emerald-400 mt-1">${ad.base_price}</p>}
            <span className="inline-flex items-center gap-1 text-[10px] text-indigo-400 font-medium mt-1 group-hover:underline">
              {ad.cta} <ChevronRight className="h-2.5 w-2.5" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}


// ============= SERVICE SIDEBAR CARD =============
function ServiceSidebarCard({ service }) {
  const productUrl = service.slug && service.city && service.category
    ? `/${service.city}/${service.category}/${service.sub_category || 'other'}/${service.slug}`
    : `/marketplace`;
  return (
    <Link to={productUrl} className="block" data-testid={`sidebar-service-${service.service_id}`}>
      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group">
        {service.image_url ? (
          <img src={service.image_url} alt={service.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0"><Tag className="h-4 w-4 text-muted-foreground" /></div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground group-hover:text-indigo-400 transition-colors line-clamp-1">{service.name}</p>
          {service.base_price > 0 && <p className="text-xs text-emerald-400">From ${service.base_price}</p>}
          {service.context && <p className="text-[10px] text-muted-foreground line-clamp-1">{service.context}</p>}
        </div>
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-indigo-400 flex-shrink-0" />
      </div>
    </Link>
  );
}


// ============= BLOG POST VIEW =============
function BlogPostView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBlog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blogs/by-slug/${slug}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load blog:', err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchBlog(); }, [fetchBlog]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-muted-foreground">Loading...</div></div>;
  if (!data?.blog) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-muted-foreground">Blog not found</div></div>;

  const { blog, mentioned_services, related_products, ads, related_blogs, word_count } = data;
  const style = styleLabels[blog.blog_style] || styleLabels.standard;
  const readTime = Math.ceil((word_count || 0) / 250);

  // Combine mentioned_services and related_products, dedup by service_id
  const allServices = (() => {
    const map = new Map();
    (mentioned_services || []).forEach(s => { if (s.service_id) map.set(s.service_id, s); });
    (related_products || []).forEach(s => { if (s.service_id && !map.has(s.service_id)) map.set(s.service_id, s); });
    return Array.from(map.values());
  })();

  // Split ads into banner and square
  const bannerAds = ads?.filter(a => a.format === 'banner') || [];
  const squareAds = ads?.filter(a => a.format === 'square') || [];

  // Split content into segments at ~1000-word intervals for ad insertion
  const getContentSegments = (htmlContent) => {
    if (!bannerAds.length || !htmlContent) return [{ type: 'html', content: htmlContent }];
    const paragraphs = htmlContent.split('</p>');
    const segments = [];
    let currentHtml = '';
    let wordCount = 0;
    let adIdx = 0;

    for (let i = 0; i < paragraphs.length; i++) {
      const pText = paragraphs[i].replace(/<[^>]*>/g, '').trim();
      const pWords = pText ? pText.split(/\s+/).length : 0;
      currentHtml += paragraphs[i] + (i < paragraphs.length - 1 ? '</p>' : '');
      wordCount += pWords;

      if (wordCount >= 1000 && adIdx < bannerAds.length) {
        segments.push({ type: 'html', content: currentHtml });
        segments.push({ type: 'ad', ad: bannerAds[adIdx] });
        currentHtml = '';
        wordCount = 0;
        adIdx++;
      }
    }
    if (currentHtml) segments.push({ type: 'html', content: currentHtml });
    return segments;
  };

  const contentSegments = getContentSegments(blog.content || '');

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="blog-post-view">
      <SEO
        title={`${blog.title} | Eventism Blog`}
        description={(blog.excerpt || blog.subtitle || blog.title || '').slice(0, 300)}
        keywords={[
          blog.title,
          blog.category,
          blog.location,
          `${blog.location || ''} event planning`,
          'event planning tips',
          'Eventism blog',
          ...(blog.tags || []),
        ].filter(Boolean)}
        image={blog.cover_image_url || blog.image_url}
        type="article"
        publishedTime={blog.published_at || blog.created_at}
        modifiedTime={blog.updated_at}
        structuredData={[
          blogPostingLD(blog),
          breadcrumbLD([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
            { name: blog.title, url: `/blog/${blog.slug}` },
          ]),
        ]}
      />
      {/* Header */}
      <div className={`relative ${blog.blog_style === 'showcase' ? 'bg-gradient-to-b from-purple-500/10 via-slate-950 to-slate-950' : 'bg-gradient-to-b from-indigo-500/5 to-slate-950'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/blog')} className="text-xs text-muted-foreground mb-4" data-testid="back-to-blog">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> All Posts
          </Button>

          <div className="flex items-center gap-2 mb-3">
            <Badge className={style.color}><style.icon className="h-3 w-3 mr-1" />{style.label}</Badge>
            <Badge variant="outline" className="text-[10px] text-muted-foreground capitalize">{blog.category}</Badge>
            {blog.location && <Badge variant="outline" className="text-[10px] text-muted-foreground"><MapPin className="h-2.5 w-2.5 mr-0.5" />{blog.location}</Badge>}
          </div>

          <h1 className={`font-heading font-bold text-foreground leading-tight ${
            blog.blog_style === 'showcase' ? 'text-3xl sm:text-4xl lg:text-5xl' : 'text-2xl sm:text-3xl lg:text-4xl'
          }`} data-testid="blog-title">
            {blog.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{blog.author}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(blog.published_at || blog.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{readTime} min read</span>
            <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{blog.views} views</span>
          </div>

          {blog.excerpt && <p className="text-base text-muted-foreground mt-4 max-w-3xl leading-relaxed">{blog.excerpt}</p>}
        </div>
      </div>

      {/* Featured Image */}
      {blog.featured_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 mb-8">
          <img src={blog.featured_image} alt={blog.title} className="w-full h-[300px] sm:h-[400px] object-cover rounded-2xl" data-testid="featured-image" />
        </div>
      )}

      {/* Mobile: Related Services (before body) */}
      {allServices.length > 0 && (
        <div className="lg:hidden max-w-5xl mx-auto px-4 sm:px-6 mb-6" data-testid="mobile-services">
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5"><Tag className="h-4 w-4 text-indigo-400" /> Featured Services</h3>
          <div className="space-y-2">
            {allServices.map((s, i) => <ServiceSidebarCard key={i} service={s} />)}
          </div>
        </div>
      )}

      {/* Main Content + Sidebar */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="lg:flex lg:gap-8">
          {/* Article Body */}
          <article className={`flex-1 min-w-0 ${
            blog.blog_style === 'showcase' ? 'blog-content blog-showcase' :
            blog.blog_style === 'listicle' ? 'blog-content blog-listicle' :
            'blog-content blog-standard'
          }`} data-testid="blog-content">
            {contentSegments.map((seg, i) =>
              seg.type === 'ad' ? (
                <BannerAd key={`ad-${i}`} ad={seg.ad} />
              ) : (
                <div key={`seg-${i}`} dangerouslySetInnerHTML={{ __html: seg.content }} />
              )
            )}
          </article>

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0 space-y-6" data-testid="desktop-sidebar">
            {/* Featured Services */}
            {allServices.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5"><Tag className="h-4 w-4 text-indigo-400" /> Featured Services</h3>
                <div className="space-y-2">
                  {allServices.map((s, i) => <ServiceSidebarCard key={i} service={s} />)}
                </div>
              </div>
            )}

            {/* Square Ads */}
            {squareAds.length > 0 && (
              <div>
                <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recommended</h4>
                <div className="space-y-3">
                  {squareAds.map(ad => <SquareAd key={ad.ad_id} ad={ad} />)}
                </div>
              </div>
            )}

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Tags</h3>
                <div className="flex flex-wrap gap-1.5">
                  {blog.tags.map(tag => (
                    <Link key={tag} to={`/blog?tag=${tag}`} className="text-[10px] px-2 py-1 rounded-full bg-muted text-muted-foreground hover:bg-accent transition-colors">
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* Bottom: Banner Ads (mobile) */}
        <div className="lg:hidden mt-8 space-y-4">
          {bannerAds.map(ad => <BannerAd key={ad.ad_id} ad={ad} />)}
          {squareAds.length > 0 && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              {squareAds.map(ad => <SquareAd key={ad.ad_id} ad={ad} />)}
            </div>
          )}
        </div>

        {/* Related Posts */}
        {related_blogs && related_blogs.length > 0 && (
          <div className="mt-12 pt-8 border-t border-border">
            <h2 className="text-lg font-bold text-foreground mb-4">Related Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related_blogs.map(rb => (
                <Link key={rb.slug} to={`/blog/${rb.slug}`} className="group" data-testid={`related-${rb.slug}`}>
                  <Card className="bg-muted/60 border-border hover:border-border transition-all overflow-hidden">
                    {rb.featured_image && <img src={rb.featured_image} alt={rb.title} className="w-full h-32 object-cover group-hover:scale-105 transition-transform" />}
                    <CardContent className="p-3">
                      <p className="text-sm font-medium text-foreground group-hover:text-indigo-400 transition-colors line-clamp-2">{rb.title}</p>
                      {rb.excerpt && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{rb.excerpt}</p>}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Blog Content Styles */}
      <style>{`
        .blog-content h2 { font-size: 1.5rem; font-weight: 700; color: white; margin: 2.5rem 0 1rem; line-height: 1.3; }
        .blog-content h3 { font-size: 1.25rem; font-weight: 600; color: #e2e8f0; margin: 2rem 0 0.75rem; }
        .blog-content p { color: #94a3b8; line-height: 1.8; margin-bottom: 1.25rem; font-size: 1rem; }
        .blog-content strong { color: #e2e8f0; font-weight: 600; }
        .blog-content em { color: #a78bfa; font-style: italic; }
        .blog-content a.inline-link, .blog-content a[href^="/services"] { color: #818cf8; text-decoration: underline; text-underline-offset: 2px; text-decoration-color: rgba(129,140,248,0.4); transition: all 0.2s; }
        .blog-content a.inline-link:hover, .blog-content a[href^="/services"]:hover { color: #a78bfa; text-decoration-color: #a78bfa; }
        .blog-content blockquote { border-left: 3px solid #6366f1; padding: 1rem 1.25rem; margin: 1.5rem 0; background: rgba(99,102,241,0.05); border-radius: 0 0.5rem 0.5rem 0; }
        .blog-content blockquote p { color: #c4b5fd; margin: 0; font-style: italic; }
        .blog-content ul, .blog-content ol { padding-left: 1.5rem; margin-bottom: 1.25rem; }
        .blog-content li { color: #94a3b8; line-height: 1.8; margin-bottom: 0.5rem; }
        .blog-content ul li { list-style-type: disc; }
        .blog-content ol li { list-style-type: decimal; }
        .blog-content img { border-radius: 0.75rem; margin: 1.5rem 0; max-width: 100%; height: auto; }

        /* Listicle */
        .blog-listicle .list-item { padding: 1.5rem; margin: 1.5rem 0; background: rgba(15,23,42,0.6); border: 1px solid rgba(51,65,85,0.5); border-radius: 1rem; }
        .blog-listicle .list-item-image { width: 100%; height: 200px; object-fit: cover; border-radius: 0.75rem; margin: 1rem 0; }

        /* Showcase */
        .blog-showcase h2 { font-size: 2rem; letter-spacing: -0.02em; }
        .blog-showcase .showcase-item { padding: 2rem; margin: 2rem 0; background: linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.05)); border: 1px solid rgba(99,102,241,0.15); border-radius: 1rem; }
        .blog-showcase .showcase-image { width: 100%; height: 280px; object-fit: cover; border-radius: 1rem; margin: 1rem 0; }
        .blog-showcase .showcase-stat { display: inline-block; padding: 0.5rem 1rem; background: rgba(99,102,241,0.1); border-radius: 0.5rem; margin: 0.5rem 0.5rem 0.5rem 0; font-weight: 700; color: #a78bfa; }
        .blog-showcase .showcase-quote { font-size: 1.3rem; border-left: 4px solid #a78bfa; }
        .blog-showcase .showcase-quote p { font-size: 1.2rem; color: #ddd6fe; }

      `}</style>
    </div>
  );
}


// ============= BLOG LIST PAGE =============
function BlogListView() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '', style: '' });

  const fetchBlogs = useCallback(async () => {
    try {
      let url = `${API}/blogs/public?limit=12`;
      if (filter.category) url += `&category=${filter.category}`;
      if (filter.style) url += `&blog_style=${filter.style}`;
      const res = await axios.get(url);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-muted-foreground">Loading...</div></div>;

  const blogs = data?.blogs || [];
  const featured = data?.featured || [];
  const categories = data?.categories || [];
  const popularTags = data?.popular_tags || [];

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="blog-list-page">
      <SEO
        title="Eventism Blog — Event Planning Tips, Trends & Inspiration"
        description="Expert event planning guides, vendor spotlights, and city-by-city insights from Eventism. Learn how to throw unforgettable parties, weddings, and corporate events."
        keywords={[
          'event planning blog',
          'event planning tips',
          'wedding planning blog',
          'corporate event ideas',
          'party planning ideas',
          'Denver event blog',
          'Las Vegas event guides',
          'Eventism blog',
          'event industry insights',
        ]}
        structuredData={[
          collectionPageLD('Eventism Blog', 'Expert event planning guides, vendor spotlights, and city-by-city insights.'),
          breadcrumbLD([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
          ]),
        ]}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground" data-testid="blog-list-title">Eventism Blog</h1>
          <p className="text-muted-foreground mt-2 text-base">Expert tips, guides, and inspiration for your events</p>
        </div>

        {/* Featured Posts */}
        {featured.length > 0 && (
          <div className="mb-10">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Featured</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featured.map(blog => {
                const s = styleLabels[blog.blog_style] || styleLabels.standard;
                return (
                  <Link key={blog.slug} to={`/blog/${blog.slug}`} className="group" data-testid={`featured-${blog.slug}`}>
                    <Card className="bg-muted/60 border-border hover:border-indigo-500/30 transition-all overflow-hidden h-full">
                      {blog.featured_image && <img src={blog.featured_image} alt={blog.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform" />}
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={`text-[9px] ${s.color}`}>{s.label}</Badge>
                        </div>
                        <h3 className="text-base font-bold text-foreground group-hover:text-indigo-400 transition-colors line-clamp-2">{blog.title}</h3>
                        {blog.excerpt && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{blog.excerpt}</p>}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <button onClick={() => setFilter({ category: '', style: '' })} className={`text-xs px-3 py-1.5 rounded-full transition-all ${!filter.category && !filter.style ? 'bg-indigo-600 text-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>All</button>
          {Object.entries(styleLabels).map(([key, s]) => (
            <button key={key} onClick={() => setFilter(p => ({ ...p, style: p.style === key ? '' : key }))}
              className={`text-xs px-3 py-1.5 rounded-full transition-all flex items-center gap-1 ${filter.style === key ? 'bg-indigo-600 text-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
              <s.icon className="h-3 w-3" /> {s.label}
            </button>
          ))}
          {categories.map(c => (
            <button key={c.name} onClick={() => setFilter(p => ({ ...p, category: p.category === c.name ? '' : c.name }))}
              className={`text-xs px-3 py-1.5 rounded-full transition-all ${filter.category === c.name ? 'bg-indigo-600 text-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
              {c.name} ({c.count})
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
          {blogs.map(blog => {
            const s = styleLabels[blog.blog_style] || styleLabels.standard;
            return (
              <Link key={blog.slug} to={`/blog/${blog.slug}`} className="group" data-testid={`blog-card-${blog.slug}`}>
                <Card className="bg-muted/60 border-border hover:border-border transition-all overflow-hidden h-full">
                  {blog.featured_image ? (
                    <img src={blog.featured_image} alt={blog.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-full h-44 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 flex items-center justify-center">
                      <BookOpen className="h-10 w-10 text-muted-foreground" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`text-[9px] ${s.color}`}>{s.label}</Badge>
                      <span className="text-[10px] text-muted-foreground">{new Date(blog.published_at || blog.created_at).toLocaleDateString()}</span>
                    </div>
                    <h3 className="text-sm font-bold text-foreground group-hover:text-indigo-400 transition-colors line-clamp-2">{blog.title}</h3>
                    {blog.excerpt && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{blog.excerpt}</p>}
                    <div className="flex items-center justify-between mt-3 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {blog.views || 0}</span>
                      <span className="flex items-center gap-1 text-indigo-400 group-hover:underline">Read more <ChevronRight className="h-3 w-3" /></span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {blogs.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="h-14 w-14 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground">No posts yet</h3>
            <p className="text-muted-foreground text-sm">Check back soon for amazing event content!</p>
          </div>
        )}

        {/* Popular Tags */}
        {popularTags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-border">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Popular Tags</h3>
            <div className="flex flex-wrap gap-2">
              {popularTags.map(t => (
                <button key={t.name} onClick={() => setFilter({ category: '', style: '' })} className="text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground hover:bg-accent transition-colors">
                  {t.name} ({t.count})
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ============= MAIN EXPORT =============
export default function BlogPage() {
  const { slug } = useParams();

  if (slug) {
    return <BlogPostView />;
  }
  return <BlogListView />;
}
